import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import methodModel from "../../methods/methods";

// ─── Icon primitives ─────────────────────────────────────────────────────────
const CheckIcon = ({ size = 10 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const SpinIcon = () => (
  <svg
    className="animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ─── Helpers ─────────────────────────────────────────────────────────────────
const formatDate = (dateString) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatCurrency = (val) => {
  const num = Number(val ?? 0);
  const truncated = Math.trunc(num * 100) / 100;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(truncated);
};

const isShipDatePassed = (shipDate) => {
  if (!shipDate) return false;
  const ship = new Date(shipDate);
  const today = new Date();
  ship.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  return ship < today;
};

const getPartialInfo = (invoice) => {
  if (invoice?.invoiceType !== "partial") return { isPartial: false };

  const partialPaid = !!invoice?.partialPaid;
  const partialMode = invoice?.partialMode || "percentage";
  const partialValue = Number(invoice?.partialValue ?? 0);
  const totalAmount = Number(
    invoice?.totalAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const minimumPartialAmt = Number(invoice?.minimumPartialAmount ?? 0);
  const remainingAmount = Number(
    invoice?.remainingAmount ?? invoice?.totalAmountDue ?? 0,
  );
  const paidAmount = Number(invoice?.paidAmount ?? 0);

  const amountToPay = partialPaid ? remainingAmount : minimumPartialAmt;

  return {
    isPartial: true,
    partialPaid,
    partialMode,
    partialValue,
    totalAmount,
    minimumPartialAmt,
    remainingAmount,
    paidAmount,
    amountToPay,
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ amountDue }) => {
  const isPaid = Number(amountDue) === 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-sm font-medium ${isPaid ? "text-[#008236]" : "text-amber-700"}`}
    >
      <span
        className={`size-1.5 rounded-full ${isPaid ? "bg-[#0a7822a6]" : "bg-amber-500"}`}
      />
      {isPaid ? "Paid" : "Due"}
    </span>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
export default function QuickBookInvoiceDetailCustomer() {
  const { id } = useParams();
  const history = useNavigate();
  const user = useSelector((state) => state.user);
  const [quotesToken, setQuotesToken] = useState("");
  const [quotesCompanyId, setQuotesCompanyId] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState({});
  const [paymentError, setPaymentError] = useState({});

  const emailParam = user?.email || (quotesToken ? "nandini@yopmail.com" : "");

  const fetchQuotes = async () => {
    await ApiClient.get("quickbook/express/tkn")
      .then((res) => {
        if (res?.success) {
          setQuotesToken(res?.data?.quick_book_accesToken);
          setQuotesCompanyId(res?.data?.quick_book_companyId);
        }
      })
      .catch(console.error);
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchInvoice = useCallback(
    async (invoiceId) => {
      setPageLoading(true);
      loader(true);
      try {
        const params = new URLSearchParams({
          id: invoiceId,
          limit: 1,
          email: emailParam,
        });
        const res = await ApiClient.get(
          `quickbooks-docs/invoices/list?${params}`,
        ).catch(() => null);

        if (res?.success) {
          const found = (res?.data || []).find(
            (inv) =>
              inv.invoiceId === invoiceId ||
              inv.id === invoiceId ||
              inv._id === invoiceId,
          );
          if (found) {
            setSelectedInvoice(found);
          } else {
            setSelectedInvoice(null);
          }
        } else {
          setSelectedInvoice(null);
        }
      } catch (err) {
        console.error("Error fetching invoice:", err);
      } finally {
        setPageLoading(false);
        loader(false);
      }
    },
    [emailParam],
  );

  useEffect(() => {
    if (id) fetchInvoice(id);
  }, [id, fetchInvoice]);

  const handlePayment = async (invoice) => {
    const p = getPartialInfo(invoice);
    const amount = p.isPartial
      ? p.amountToPay
      : invoice.totalAmountDue || invoice.amountDue || 0;

    setPaymentLoading((prev) => ({ ...prev, [invoice.invoiceId]: true }));
    setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" }));

    const payload = {
      name: invoice.contact?.Name || user?.name || "Customer",
      email:
        invoice.BillEmail?.Address ||
        invoice.BillEmail ||
        user?.email ||
        "customer@example.com",
      mobileNo:
        invoice.contact?.Mobile || invoice.contact?.Phone || user?.phone || "",
      invoiceId: invoice.invoiceId,
      quoteId: invoice.EstimateId || "",
      amount: amount?.toString() || "0",
      CustomerRef: invoice.CustomerRef || invoice.customerId || "",
    };

    try {
      const res = await ApiClient.post(
        "quickbook/invoice/payment/link",
        payload,
      );
      if (res?.success && res?.data) {
        window.open(res?.data?.paymentUrl, "_blank");
      } else {
        setPaymentError((prev) => ({
          ...prev,
          [invoice.invoiceId]:
            res?.message || "Failed to generate payment link",
        }));
        setTimeout(
          () =>
            setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" })),
          3000,
        );
      }
    } catch {
      setPaymentError((prev) => ({
        ...prev,
        [invoice.invoiceId]: "Something went wrong. Please try again.",
      }));
      setTimeout(
        () => setPaymentError((prev) => ({ ...prev, [invoice.invoiceId]: "" })),
        3000,
      );
    } finally {
      setPaymentLoading((prev) => ({ ...prev, [invoice.invoiceId]: false }));
    }
  };

  return (
    <>
      <Header />
      <div className="max-w-[800px] mx-auto px-5 py-10 md:pb-20">
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => history("/orders?tab=invoice")}
            className="size-9 rounded-xl border border-gray-200 hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <div>
            <h2 className="text-gray-900 text-xl font-semibold">
              Invoice Details
            </h2>
            <p className="text-gray-500 text-sm">
              View your invoice and pay your balance
            </p>
          </div>
        </div>

        <div className="w-full max-w-2xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden">
          {pageLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="size-8 border-2 border-[#115B9D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !selectedInvoice ? (
            <div className="flex items-center justify-center py-20">
              <p className="text-gray-500 text-sm">Invoice not found.</p>
            </div>
          ) : (
            (() => {
              const amountDueRaw = Number(
                selectedInvoice?.totalAmountDue ||
                  selectedInvoice?.amountDue ||
                  0,
              );
              const isPaid = amountDueRaw === 0;
              const p = getPartialInfo(selectedInvoice);

              const invoiceLines =
                selectedInvoice?.estimate?.raw?.Line ||
                selectedInvoice?.raw?.Line ||
                [];
              const salesLines = invoiceLines.filter(
                (l) => l.DetailType === "SalesItemLineDetail",
              );
              const discountLine = invoiceLines.find(
                (item) => item?.DetailType === "DiscountLineDetail",
              );
              const discount = Number(
                discountLine?.Amount || discountLine?.LineAmount || 0,
              );
              const subtotal = salesLines.reduce(
                (s, l) => s + Number(l.Amount || 0),
                0,
              );
              const tax = Number(selectedInvoice?.totalTax || 0);
              const totalInvoice = Number(
                selectedInvoice?.totalAmount ||
                  selectedInvoice?.raw?.TotalAmt ||
                  subtotal + tax,
              );

              return (
                <>
                  {/* Header */}
                  <div className="px-6 pt-6 pb-5 text-center">
                    <div className="w-12 h-12 bg-[#115B9D] rounded-full flex items-center justify-center mx-auto mb-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                        <polyline points="14 2 14 8 20 8" />
                        <line x1="16" y1="13" x2="8" y2="13" />
                        <line x1="16" y1="17" x2="8" y2="17" />
                        <polyline points="10 9 9 9 8 9" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold tracking-wide">
                      Invoice
                    </h2>
                    <p className="mt-1 text-[11px] font-semibold tracking-[2px] text-[#115B9D] uppercase">
                      #{selectedInvoice?.invoiceNumber}
                    </p>
                    <div className="mt-4">
                      <p className="text-sm">
                        Hello,{" "}
                        <span className="font-bold capitalize">
                          {selectedInvoice.contact?.Name || "Customer"}
                        </span>{" "}
                        👋
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Here are your invoice details. Use the pay button below
                        to settle your balance.
                      </p>
                    </div>
                  </div>

                  {/* Dates + Status */}
                  <div className="px-5 pb-4 border-b border-gray-100">
                    <div className="border border-gray-200 rounded-xl grid grid-cols-2 sm:grid-cols-4 items-stretch bg-[#115B9D08]">
                      {[
                        {
                          label: "Created",
                          value: formatDate(
                            selectedInvoice.createdAtQb ||
                              selectedInvoice.createdAt,
                          ),
                          color: "text-gray-800",
                        },
                        {
                          label: "Due Date",
                          value: formatDate(selectedInvoice.dueDate),
                          color: "text-red-500",
                        },
                        {
                          label: "Ship Date",
                          value: formatDate(selectedInvoice.shipDate),
                          color: isShipDatePassed(selectedInvoice.shipDate)
                            ? "text-red-500"
                            : "text-gray-800",
                        },
                      ].map((item) => (
                        <div
                          key={item.label}
                          className="py-4 px-3 border-r border-gray-200"
                        >
                          <p className="text-[9px] font-semibold tracking-widest text-gray-400 uppercase mb-1">
                            {item.label}
                          </p>
                          <p className={`text-sm font-bold ${item.color}`}>
                            {item.value}
                          </p>
                        </div>
                      ))}
                      <div className="py-4 px-3 flex items-center justify-center">
                        <StatusBadge amountDue={amountDueRaw} />
                      </div>
                    </div>
                  </div>

                  {/* Partial payment flow */}
                  {p.isPartial && !isPaid && (
                    <div className="px-5 py-4 border-b border-gray-100">
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-3">
                        Payment Schedule
                      </p>

                      <div className="mb-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs text-blue-800 leading-relaxed">
                        <span className="font-semibold">How this works:</span>{" "}
                        This invoice uses a two-step partial payment plan.
                        {p.partialMode === "percentage"
                          ? ` Pay ${p.partialValue}% upfront (${formatCurrency(p.minimumPartialAmt)}), then the remaining ${formatCurrency(p.remainingAmount - p.minimumPartialAmt > 0 ? p.remainingAmount : p.totalAmount - p.minimumPartialAmt)} after.`
                          : ` Pay the fixed partial amount (${formatCurrency(p.minimumPartialAmt)}) upfront, then the remaining balance after.`}
                      </div>

                      {/* Step 1 */}
                      <div
                        className={`flex items-start gap-3 p-3.5 rounded-t-xl border border-b-0 ${p.partialPaid ? "bg-[#F0FEF4] border-green-200" : "bg-amber-50 border-amber-200"}`}
                      >
                        <div
                          className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${p.partialPaid ? "bg-[#008236]" : "bg-amber-500"}`}
                        >
                          {p.partialPaid ? <CheckIcon /> : "1"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p
                              className={`text-sm font-semibold ${p.partialPaid ? "text-[#008236]" : "text-amber-700"}`}
                            >
                              {p.partialPaid
                                ? "Partial payment received ✓"
                                : "Step 1 — Pay partial amount"}
                            </p>
                            <p
                              className={`text-sm font-bold shrink-0 ${p.partialPaid ? "text-[#008236]" : "text-amber-700"}`}
                            >
                              {formatCurrency(p.minimumPartialAmt)}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {p.partialMode === "percentage"
                              ? `${p.partialValue}% of total invoice ${formatCurrency(p.totalAmount)}`
                              : "Fixed partial amount as agreed"}
                          </p>
                        </div>
                      </div>

                      {/* Step 2 */}
                      <div
                        className={`flex items-start gap-3 p-3.5 rounded-b-xl border ${p.partialPaid ? "bg-[#EFF6FF] border-blue-200" : "bg-gray-50 border-gray-200"}`}
                      >
                        <div
                          className={`mt-0.5 shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${p.partialPaid ? "bg-[#115B9D]" : "bg-gray-300"}`}
                        >
                          2
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <p
                              className={`text-sm font-semibold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                            >
                              {p.partialPaid
                                ? "Step 2 — Pay remaining balance"
                                : "Remaining balance (unlocks after Step 1)"}
                            </p>
                            <p
                              className={`text-sm font-bold shrink-0 ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                            >
                              {formatCurrency(
                                p.totalAmount - p.minimumPartialAmt,
                              )}
                            </p>
                          </div>
                          <p
                            className={`text-xs mt-0.5 ${p.partialPaid ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {p.partialPaid
                              ? "Pay now to fully clear this invoice."
                              : "Complete Step 1 first to unlock this payment."}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Invoice Items */}
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5">
                      Invoice Items
                    </p>
                    <div className="border border-gray-200 rounded-xl overflow-auto">
                      <table className="w-full text-sm min-w-[480px]">
                        <thead className="bg-[#115B9D]">
                          <tr>
                            <th className="py-2.5 px-4 text-left text-[10px] font-semibold text-white tracking-wide">
                              #
                            </th>
                            <th className="py-2.5 px-4 text-left text-[10px] font-semibold text-white tracking-wide">
                              Description
                            </th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                              Qty
                            </th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                              Unit Price
                            </th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-semibold text-white tracking-wide">
                              Amount
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {salesLines.length === 0 ? (
                            <tr>
                              <td
                                colSpan={5}
                                className="py-4 px-4 text-center text-sm text-gray-400"
                              >
                                No items
                              </td>
                            </tr>
                          ) : (
                            salesLines.map((li, i) => (
                              <tr
                                key={i}
                                className="hover:bg-gray-50/60 transition-colors"
                              >
                                <td className="py-3 px-4 text-gray-400">
                                  {i + 1}
                                </td>
                                <td className="py-3 px-4 text-gray-700">
                                  {li.Description ||
                                    li.SalesItemLineDetail?.ItemRef?.name ||
                                    "—"}
                                </td>
                                <td className="py-3 px-4 text-right text-gray-600">
                                  {li.SalesItemLineDetail?.Qty ?? "—"}
                                </td>
                                <td className="py-3 px-4 text-right text-gray-600">
                                  {formatCurrency(
                                    li.SalesItemLineDetail?.UnitPrice,
                                    selectedInvoice.currencyCode,
                                  )}
                                </td>
                                <td className="py-3 px-4 text-right font-semibold text-gray-800">
                                  {formatCurrency(
                                    li.Amount,
                                    selectedInvoice.currencyCode,
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Summary */}
                  <div className="px-5 py-4 border-b border-gray-100">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-2.5">
                      Summary
                    </p>
                    <div className="border border-gray-100 rounded-xl overflow-hidden divide-y divide-gray-100">
                      <div className="flex items-center justify-between px-4 py-3">
                        <p className="text-sm text-gray-500">Subtotal</p>
                        <p className="text-sm font-semibold text-gray-700">
                          {formatCurrency(
                            subtotal,
                            selectedInvoice?.currencyCode,
                          )}
                        </p>
                      </div>
                      <div className="flex items-center justify-between px-4 py-3">
                        <p className="text-sm text-gray-500">Tax</p>
                        <p className="text-sm font-semibold text-gray-700">
                          {formatCurrency(tax, selectedInvoice?.currencyCode)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between px-4 py-3">
                        <p className="text-sm text-gray-500">Discount</p>
                        <p className="text-sm font-semibold text-gray-700">
                          -
                          {formatCurrency(
                            discount,
                            selectedInvoice?.currencyCode,
                          )}
                        </p>
                      </div>
                      <div className="flex items-center justify-between px-4 py-3">
                        <p className="text-sm text-gray-500">Invoice Total</p>
                        <p className="text-sm font-semibold text-gray-800">
                          {formatCurrency(
                            totalInvoice,
                            selectedInvoice?.currencyCode,
                          )}
                        </p>
                      </div>

                      {p.isPartial && !isPaid && (
                        <>
                          <div className="flex items-center justify-between px-4 py-3 bg-amber-50">
                            <div>
                              <p className="text-sm text-amber-700 font-medium">
                                {p.partialPaid
                                  ? "Partial already paid"
                                  : `Step 1 — Pay now (${p.partialMode === "percentage" ? `${p.partialValue}%` : "fixed"})`}
                              </p>
                              {!p.partialPaid && (
                                <p className="text-xs text-amber-600 mt-0.5">
                                  Due immediately
                                </p>
                              )}
                            </div>
                            <p className="text-sm font-bold text-amber-700">
                              {formatCurrency(
                                p.minimumPartialAmt,
                                selectedInvoice?.currencyCode,
                              )}
                            </p>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-blue-50/60">
                            <div>
                              <p
                                className={`text-sm font-medium ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                              >
                                {p.partialPaid
                                  ? "Step 2 — Remaining balance due"
                                  : "Remaining balance (locked)"}
                              </p>
                              {!p.partialPaid && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  Unlocks after Step 1
                                </p>
                              )}
                            </div>
                            <p
                              className={`text-sm font-bold ${p.partialPaid ? "text-[#115B9D]" : "text-gray-400"}`}
                            >
                              {formatCurrency(
                                p.totalAmount - p.minimumPartialAmt,
                                selectedInvoice?.currencyCode,
                              )}
                            </p>
                          </div>
                        </>
                      )}

                      {isPaid && (
                        <div className="flex items-center justify-between px-4 py-3 bg-[#F0FEF4]">
                          <p className="text-sm text-[#008236] font-medium">
                            Total Paid
                          </p>
                          <p className="text-sm font-bold text-[#008236]">
                            {formatCurrency(
                              totalInvoice,
                              selectedInvoice?.currencyCode,
                            )}
                          </p>
                        </div>
                      )}

                      <div className="flex items-center justify-between px-4 py-3.5 bg-[#115B9D] rounded-b-xl">
                        <p className="text-sm font-bold text-white tracking-wide">
                          {isPaid
                            ? "Fully Paid ✓"
                            : p.isPartial
                              ? p.partialPaid
                                ? "Balance Due (Step 2)"
                                : "Due Now (Step 1)"
                              : "Total Due"}
                        </p>
                        <p className="text-base font-bold text-white">
                          {isPaid
                            ? formatCurrency(
                                totalInvoice,
                                selectedInvoice?.currencyCode,
                              )
                            : formatCurrency(
                                p.isPartial ? p.amountToPay : amountDueRaw,
                                selectedInvoice?.currencyCode,
                              )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Pay CTA */}
                  {!isPaid && (
                    <div className="mx-5 my-5 border-2 border-dashed border-green-300 rounded-2xl px-5 py-5 text-center bg-[#f0fdf4]">
                      <p className="text-[10px] font-bold tracking-[2px] uppercase text-green-700 mb-1">
                        {p.isPartial
                          ? p.partialPaid
                            ? "Step 2 of 2 — Pay Remaining Balance"
                            : "Step 1 of 2 — Partial Payment Required"
                          : "Ready to Pay?"}
                      </p>
                      <p className="text-xs text-green-700/80 mb-4 leading-relaxed max-w-sm mx-auto">
                        {p.isPartial
                          ? p.partialPaid
                            ? `Your partial payment of ${formatCurrency(p.minimumPartialAmt)} was received. Pay the remaining ${formatCurrency(p.totalAmount - p.minimumPartialAmt)} to fully clear this invoice.`
                            : `A minimum of ${formatCurrency(p.minimumPartialAmt)} (${p.partialMode === "percentage" ? `${p.partialValue}% of ${formatCurrency(p.totalAmount)}` : "fixed amount"}) is required now. The remaining ${formatCurrency(p.totalAmount - p.minimumPartialAmt)} can be paid in Step 2.`
                          : "Click below to complete your payment securely."}
                      </p>

                      <button
                        type="button"
                        onClick={() => handlePayment(selectedInvoice)}
                        disabled={paymentLoading[selectedInvoice?.invoiceId]}
                        className="inline-flex items-center gap-2 bg-[#008236] hover:bg-[#016f2f] active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm px-7 py-3 rounded-full transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                      >
                        {paymentLoading[selectedInvoice?.invoiceId] ? (
                          <>
                            <SpinIcon /> Processing…
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect
                                x="1"
                                y="11"
                                width="22"
                                height="11"
                                rx="2"
                                ry="2"
                              />
                              <path d="M1 11V8a2 2 0 0 1 2-2h4l2-4 2 4h4a2 2 0 0 1 2 2v3" />
                            </svg>
                            {p.isPartial
                              ? `Pay ${p.partialPaid ? "Balance" : "Step 1"} — ${formatCurrency(p.amountToPay, selectedInvoice?.currencyCode)}`
                              : `Pay Now — ${formatCurrency(amountDueRaw, selectedInvoice?.currencyCode)}`}
                          </>
                        )}
                      </button>
                      {paymentError[selectedInvoice?.invoiceId] && (
                        <p className="mt-2 text-xs text-red-600">
                          {paymentError[selectedInvoice?.invoiceId]}
                        </p>
                      )}
                      <div className="flex justify-center items-center gap-2 mt-2.5">
                        <span className="text-[10px] text-[#008236]">
                          🔒 Secure payment
                        </span>
                        <span className="text-green-300">·</span>
                        <span className="text-[10px] text-[#008236]">
                          Your data is protected
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Addresses */}
                  <div className="flex gap-4 px-5 pb-5 flex-wrap sm:flex-nowrap">
                    {[
                      {
                        title: "📍 Billing Address",
                        addr: selectedInvoice?.raw?.BillAddr,
                      },
                      {
                        title: "🚚 Shipping Address",
                        addr: selectedInvoice?.raw?.ShipAddr,
                      },
                    ].map(({ title, addr }) => (
                      <div
                        key={title}
                        className="flex-1 min-w-[180px] bg-white border border-[#115B9D]/30 p-4 rounded-xl"
                      >
                        <h2 className="text-xs font-semibold text-[#115B9D] border-b border-[#115B9D]/30 pb-2 mb-2 uppercase tracking-wide">
                          {title}
                        </h2>
                        <div className="space-y-0.5 text-sm text-gray-600">
                          {[
                            addr?.Line1,
                            addr?.City || addr?.Line2,
                            addr?.State,
                            addr?.Country || addr?.Line3,
                            addr?.PostalCode,
                          ]
                            .filter(Boolean)
                            .map((line, i) => (
                              <p key={i}>{line}</p>
                            ))}
                          {!addr?.Line1 && (
                            <p className="text-gray-400 text-xs">
                              No address on file
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Footer */}
                  <div className="bg-[#115B9D] px-5 py-3">
                    <p className="text-center text-[11px] text-white">
                      © 2026 · All rights reserved
                    </p>
                  </div>
                </>
              );
            })()
          )}
        </div>
      </div>
      {methodModel.TokenChecker() && <Footer />}
    </>
  );
}
